import { useState, useEffect } from 'react';
import { Popup } from 'devextreme-react/popup';
import SelectBox from 'devextreme-react/select-box';
import { Button } from 'devextreme-react';
import { useAppDispatch } from 'store/app/hooks';
import { showErrorSnackbar } from 'cui/components/snackbar/Snackbar.slice';
import './ReportsClientPopUp.scss';
import { useNavigate } from 'react-router-dom';
import {
    setReportClientDropDownData,
    setSelectedReportClient,
    setSelectedReportEngagement,
    setReportEngagementDropDownData
} from 'cui/components/breadcrumb/breadcrumb.slice';
import { ErrorCodesUI } from 'constants/ErrorCodes';
import { selectedApplication } from 'store/app/applications.slice';
import { useInsightApi } from 'hooks/use-insight-api';

type ReportsClientBreadcrumbPopUpProps = {
    client: { key: string; value: string };
    applicationId: string;
    applicationName: string;
    isVisible: boolean;
    onClose: () => void;
    clientsData: { key: string; value: string }[];
};

const ReportsClientBreadcrumbPopUp = ({ client, applicationId, applicationName, isVisible, onClose, clientsData }: ReportsClientBreadcrumbPopUpProps) => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const [selectedEngagement, setSelectedEngagement] = useState<string | null>(null);
    const [engagementsList, setEngagementsList] = useState<any>([]);
    const { getEngagementsForUserReports } = useInsightApi();

    useEffect(() => {
        const fetchEngagements = async () => {
            if (client && client.key) {
                const engagements = await getEngagementsForUserReports(applicationId, client.key);
                if (engagements.length > 0) {
                    setEngagementsList(engagements.map((engagement: any) => ({
                        key: engagement.key,
                        value: engagement.value
                    })));
                } else {
                    setEngagementsList([]);
                }
            }
        };
        if (isVisible) {
            setSelectedEngagement(null);
            if (!engagementsList.length || (engagementsList.length && engagementsList[0]?.key !== client.key)) {
                fetchEngagements();
            }
        }
    }, [client.key, applicationId, isVisible]);

    const handleConfirm = () => {
        if (!selectedEngagement) {
            showErrorSnackbar(dispatch, `${ErrorCodesUI.NoEngagementSelected} - Please select an engagement.`);
            return;
        }
        dispatch(selectedApplication(applicationId));
        dispatch(setReportClientDropDownData(clientsData.map((c) => ({
            id: c.key,
            value: c.value
        }))));
        dispatch(setSelectedReportClient(client.key));
        dispatch(setReportEngagementDropDownData(engagementsList.map((engagement: any) => ({
            id: engagement.key,
            value: engagement.value
        }))));
        dispatch(setSelectedReportEngagement(selectedEngagement));
        navigate(`applications/${applicationId}/${client.key}/${selectedEngagement}`);
        onClose();
    };

    const onCancel = () => {
        setSelectedEngagement(null);
        onClose();
    };

    return (
        <Popup
            visible={isVisible}
            onHiding={onCancel}
            showCloseButton={true}
            showTitle={true}
            title={`Select Engagement${typeof applicationName === 'string' ? ` for ${applicationName}` : ''}`}
            width={400}
            height={250}
            dragEnabled={false}
        >
            <div>
                <div className="input-group">
                    <label className="text-label">Selected Client:</label>
                    <SelectBox
                        dataSource={[client]}
                        displayExpr="value"
                        valueExpr="key"
                        value={client.key}
                        stylingMode="outlined"
                        disabled={true}
                    />
                </div>
                <div className="input-group">
                    <label className="text-label">Select Engagement:</label>
                    <SelectBox
                        dataSource={engagementsList}
                        displayExpr="value"
                        valueExpr="key"
                        value={selectedEngagement}
                        onValueChange={(value) => setSelectedEngagement(value)}
                        minSearchLength={3}
                        stylingMode="outlined"
                        placeholder={engagementsList.length === 0
                            ? "Loading engagements..."
                            : "Type min. 3 chars to search for an engagement"}
                        searchEnabled={true}
                        disabled={engagementsList.length === 0}
                    />
                </div>
                <div className="popup-buttons">
                    <Button
                        text="Cancel"
                        type="normal"
                        stylingMode="contained"
                        onClick={onCancel}
                    />
                    <Button
                        text="Submit"
                        type="default"
                        stylingMode="contained"
                        onClick={handleConfirm}
                    />
                </div>
            </div>
        </Popup>
    );
};

export default ReportsClientBreadcrumbPopUp;
